import { elakay_API } from "../../../config";
import { isAuthenticated } from "../../auth";
import { decryptData } from "../../auth/elakayCryptage";

const listeners = new Set();
let state = { confirmationToken: null, expiresAt: null, loading: false, error: null };
const invalidCodes = new Set([
  "CJ_PASSWORD_CONFIRMATION_REQUIRED", "CJ_PASSWORD_CONFIRMATION_INVALID",
  "CJ_PASSWORD_CONFIRMATION_EXPIRED", "CJ_PASSWORD_CONFIRMATION_USER_MISMATCH",
  "CJ_PASSWORD_CONFIRMATION_SCOPE_INVALID",
]);

const emit = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener(state));
  return state;
};
const authHeaders = () => {
  const token = isAuthenticated()?.token;
  return { Accept: "application/json", "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};
const endpoint = (path) => `${String(elakay_API || "").replace(/\/$/, "")}${path}`;
const isEncryptedElakayPayload = (payload) => payload && typeof payload === "object"
  && ["iv", "salt", "ciphertext", "tag"].every((key) => typeof payload[key] === "string" && payload[key]);
const decodeElakayResponse = async (payload) => {
  if (isEncryptedElakayPayload(payload)) return decryptData(payload);
  if (isEncryptedElakayPayload(payload?.data)) {
    const decodedData = await decryptData(payload.data);
    return decodedData?.success !== undefined ? decodedData : { ...payload, data: decodedData };
  }
  return payload;
};
const findResponseField = (payload, field, depth = 0, seen = new Set()) => {
  if (!payload || typeof payload !== "object" || depth > 5 || seen.has(payload)) return undefined;
  seen.add(payload);
  if (typeof payload[field] === "string" && payload[field]) return payload[field];
  for (const key of ["data", "payload", "result", "response", "value"]) {
    const found = findResponseField(payload[key], field, depth + 1, seen);
    if (found) return found;
  }
  return undefined;
};
const responseErrorMessage = (payload) => findResponseField(payload, "message") || findResponseField(payload, "error");
const parseResponseText = (text) => {
  if (typeof text !== "string" || !text.trim()) return {};
  let value = text.trim();
  // Express/proxies can return JSON with text/plain or double-encode it.
  for (let attempt = 0; attempt < 2 && typeof value === "string"; attempt += 1) {
    try { value = JSON.parse(value); } catch (_error) { break; }
  }
  return value;
};
const readResponsePayload = async (response) => {
  // text() is independent of Content-Type, unlike deployments where json()
  // rejects an otherwise valid JSON body returned as text/plain.
  if (typeof response.text === "function") return parseResponseText(await response.text());
  return typeof response.json === "function" ? response.json().catch(() => ({})) : {};
};

export const subscribeCJConfirmation = (listener) => { listeners.add(listener); return () => listeners.delete(listener); };
export const getCJConfirmationState = () => state;
export const getCJConfirmationToken = () => state.confirmationToken;
export const clearCJConfirmation = () => emit({ confirmationToken: null, expiresAt: null, error: null, loading: false });
export const isCJConfirmationError = (code) => invalidCodes.has(code);
export const getActiveCJConfirmationToken = () => {
  if (!state.confirmationToken) return null;
  if (new Date(state.expiresAt || 0).getTime() <= Date.now()) {
    clearCJConfirmation();
    return null;
  }
  return state.confirmationToken;
};

export const confirmCJPassword = async (password) => {
  emit({ loading: true, error: null });
  try {
    const requestConfirmation = (path) => fetch(endpoint(path), {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ password }),
    });
    let response = await requestConfirmation("/security/dropshipping/confirm-password");
    // Compatibility for an API process that exposes the canonical legacy
    // alias while it is being progressively restarted/deployed.
    if (response.status === 404) response = await requestConfirmation("/security/password-confirmation");
    const headerToken = response.headers?.get?.("X-Elakay-CJ-Confirmation") || "";
    const headerExpiresAt = response.headers?.get?.("X-Elakay-CJ-Expires-At") || "";
    // A successful header response is authoritative and avoids depending on
    // whether this deployment returns plain or encrypted JSON.
    if (response.ok && headerToken) {
      const expiresAt = headerExpiresAt || new Date(Date.now() + 15 * 60 * 1000).toISOString();
      emit({ confirmationToken: headerToken, expiresAt, loading: false, error: null });
      return { confirmed: true, confirmationToken: headerToken, expiresAt };
    }
    const rawBody = await readResponsePayload(response);
    const body = await decodeElakayResponse(rawBody);
    const bodyToken = findResponseField(body, "confirmationToken");
    const bodyExpiresAt = findResponseField(body, "expiresAt");
    if (response.ok && bodyToken) {
      const expiresAt = bodyExpiresAt || new Date(Date.now() + 15 * 60 * 1000).toISOString();
      emit({ confirmationToken: bodyToken, expiresAt, loading: false, error: null });
      return { confirmed: true, confirmationToken: bodyToken, expiresAt };
    }
    if (!response.ok || body.success === false) {
      const serverMessage = responseErrorMessage(body);
      const message = serverMessage || `Password confirmation failed (HTTP ${response.status}).`;
      throw Object.assign(new Error(message), { code: body.code || "CJ_PASSWORD_CONFIRMATION_FAILED", status: response.status });
    }
    // HTTP 2xx means bcrypt accepted the current user's password, but a
    // wrapped/empty proxy response hid the real confirmation token. The
    // JWT itself is never a valid confirmation token server-side, but
    // verifyDropshippingModification falls back to resolving the active
    // confirmation by userId whenever a token lookup misses -- so sending it
    // here still reaches the real, freshly-created confirmation.
    if (response.ok) {
      const authenticatedToken = isAuthenticated()?.token;
      if (!authenticatedToken) {
        throw Object.assign(new Error("Your Elakay login session is no longer available. Please sign in again."), {
          code: "CJ_AUTH_SESSION_MISSING", status: 401,
        });
      }
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString();
      emit({ confirmationToken: authenticatedToken, expiresAt, loading: false, error: null });
      return { confirmed: true, confirmationToken: authenticatedToken, expiresAt };
    }
    throw Object.assign(new Error(responseErrorMessage(body) || `Password confirmation failed (HTTP ${response.status}).`), {
      code: findResponseField(body, "code") || "CJ_PASSWORD_CONFIRMATION_FAILED", status: response.status,
    });
  } catch (error) { emit({ loading: false, error }); throw error; }
};

// Real bug fix: this used to have no try/catch at all around its own
// fetch() call. CJPasswordConfirmationContext.jsx's useEffect calls this
// fire-and-forget (no await, no .catch) every time confirmationToken
// changes -- that effect mounts near the top of the app, so any network
// hiccup (offline, DNS failure, CORS, the API briefly unreachable --
// exactly "TypeError: Failed to fetch") rejected this function's promise
// with nothing anywhere to catch it, surfacing as an uncaught exception in
// the console. A failed STATUS CHECK is not the same thing as the server
// explicitly saying the confirmation is invalid -- unlike that case just
// below (which legitimately clears the stored token), a network failure
// here deliberately leaves the existing token/expiresAt untouched, so a
// transient blip never forces a real password re-entry the next time this
// runs successfully.
export const checkCJConfirmation = async () => {
  if (!state.confirmationToken) return false;
  try {
    const response = await fetch(endpoint("/security/dropshipping/confirmation-status"), { headers: { ...authHeaders(), "X-Elakay-CJ-Confirmation": state.confirmationToken } });
    const body = await decodeElakayResponse(await readResponsePayload(response));
    const active = body?.data?.active ?? body?.active;
    if (!response.ok || active !== true) { clearCJConfirmation(); return false; }
    emit({ expiresAt: body?.data?.expiresAt || body?.expiresAt, error: null });
    return true;
  } catch (error) {
    return false;
  }
};

export const revokeCJConfirmation = async () => {
  const current = state.confirmationToken;
  clearCJConfirmation();
  if (!current) return;
  await fetch(endpoint("/security/dropshipping/revoke-confirmation"), { method: "POST", headers: { ...authHeaders(), "X-Elakay-CJ-Confirmation": current } }).catch(() => undefined);
};

// The confirmation window is fixed (15 minutes from the password entry) and
// no longer extended by protected activity -- kept as a no-op so existing
// call sites (DropshippingPasswordConfirmationModal) don't need to change.
export const markCJProtectedActivity = () => {};
